import React from 'react';
import { StandardPdfTemplate } from '../../templates/StandardPdfTemplate';
import { ProjectData, ProjectInfoData } from '../../shared/types/PdfCommonTypes';
import { TFunction } from 'i18next';
import { IGeogridData } from '../../../../api/IApiResponse';
import { PavedResultsCore } from './sections/PavedResultsCore';
import { PavedGeoGridCore } from './sections/PavedGeoGridCore';

interface PavedCombinedResultsPdfDocumentProps {
    projectInfo: any;
    dynamicProject: any;
    output: any;
    tireImageUnreinforced?: string;
    tireImageReinforced?: string;
    tireImageGeogrid?: string;
    chartImage?: string;
    geoGridData?: IGeogridData;
    geogridImage?: string;
    trafficData?: {
        unstabilized?: any;
        increasedPassages?: any;
        optimized?: any;
    };
    // Dynamic design sections - optional for backward compatibility
    surfaceDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
    };
    baseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
    };
    subbaseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
    };
    t: TFunction;
}

const PavedCombinedResultsPdfDocumentV2: React.FC<PavedCombinedResultsPdfDocumentProps> = ({
    projectInfo,
    dynamicProject,
    output,
    tireImageUnreinforced,
    tireImageReinforced,
    tireImageGeogrid,
    chartImage,
    geoGridData,
    geogridImage,
    trafficData,
    surfaceDesignImages,
    baseDesignImages,
    subbaseDesignImages,
    t,
}) => {
    const projectData = dynamicProject?.data;
    const projectType = 'paved';
    const unitSystem = projectData?.unit || 'Metric';

    // Prepare data for StandardPdfTemplate
    const projectDataForTemplate: ProjectData = {
        projectName: projectData?.projectName || 'N/A',
        projectType: projectType,
        unit: unitSystem,
        mapImage: projectData?.mapImage,
    };

    const projectInfoDataForTemplate: ProjectInfoData = {
        data: {
            nrProject: projectInfo?.nrProject || projectData?.projectInfo?.data?.nrProject || 'N/A',
        },
        projectOwner: projectInfo?.projectOwner || projectData?.projectInfo?.projectOwner || 'N/A',
        designerFirmName: projectInfo?.designerFirmName || projectData?.projectInfo?.designerFirmName || 'N/A',
        designerFirmCountry: projectInfo?.designerFirmCountry || projectData?.projectInfo?.designerFirmCountry || 'N/A',
        designerFirmCity: projectInfo?.designerFirmCity || projectData?.projectInfo?.data?.designerFirmCity || 'N/A',
        coordinates: projectInfo?.coordinates || projectData?.projectInfo?.coordinates || [null, null],
    };

    return (
        <StandardPdfTemplate
            projectType={projectType}
            projectData={projectDataForTemplate}
            projectInfoData={projectInfoDataForTemplate}
            t={t}
            headerConfig={{
                customTitle: 'GROUNDMAC - design Tecnical Report',
                customProjectType: t('project_type_road_paved') || 'Road Paved',
                showGeogridSubtitle: true,
            }}
            footerConfig={{
                customText: t('pdf_footer') || 'MACread eDesign',
                customNotice: t('pdf_footer_notice') || 'Footer notice',
            }}
            showProjectInfo={true}>
            {/* Use the shared core component for Paved Results */}
            <PavedResultsCore
                output={output}
                projectData={projectData}
                // ✅ ISSUE-001 FIX: Provide fallbacks for missing images (graceful degradation)
                tireImageUnreinforced={tireImageUnreinforced || ''}
                tireImageReinforced={tireImageReinforced || ''}
                tireImageGeogrid={tireImageGeogrid || ''}
                chartImage={chartImage || ''}
                trafficData={trafficData}
                surfaceDesignImages={surfaceDesignImages}
                baseDesignImages={baseDesignImages}
                subbaseDesignImages={subbaseDesignImages}
                t={t}
                showInputData={true}
                showChart={true}
                showResults={true}
                customTitle="GROUNDMAC - design Tecnical Report"
            />

            {/* Use the shared core component for GeoGrid Results */}
            <PavedGeoGridCore
                geoGridData={geoGridData}
                geogridImage={geogridImage}
                projectData={projectData}
                t={t}
                showInputData={true}
                showGeoGridImage={true}
                showCourseResults={true}
                customTitle="GeoGrid Reinforcement Analysis"
            />
        </StandardPdfTemplate>
    );
};

export default PavedCombinedResultsPdfDocumentV2;
